/*! npc-product-card.js
 *  ONE shared script for every product card (replaces one <script> per card).
 *
 *  - Does nothing at page load except register a few delegated listeners.
 *  - A card is "activated" (listeners attached, remaining images loaded) the first
 *    time the shopper touches, clicks or focuses it.
 *  - Slides 2+ keep their image URL in data-src; they are fetched on interaction,
 *    or after the first page interaction for cards that are on screen.
 */
(() => {
  'use strict';

  if (window.NPCProductCards) return;

  const CARD = '.npc-product-card';
  const DRAG_THRESHOLD = 8;
  const CLICK_SUPPRESS_TIME = 500;

  /* Progress thumb is 15% wide (see CSS). translateX(%) is relative to the thumb's
     own width, so full travel = (100% - 15%) / 15% = 566.6667% — no measuring needed. */
  const THUMB_TRAVEL = 566.6667;

  const activated = new WeakSet();
  const warmed = new WeakSet();

  /* ------------------------------------------------------------------
     IMAGE LOADING
     ------------------------------------------------------------------ */
  const loadImg = (img) => {
    const src = img.getAttribute('data-src');
    if (!src) return;
    const srcset = img.getAttribute('data-srcset');
    if (srcset) img.srcset = srcset;
    img.src = src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  };

  const loadAll = (card) => card.querySelectorAll('img[data-src]').forEach(loadImg);

  const loadNext = (card) => {
    const img = card.querySelector('img[data-src]');
    if (img) loadImg(img);
  };

  const warm = (card) => {
    if (warmed.has(card)) return;
    warmed.add(card);
    loadNext(card);
  };

  /* ------------------------------------------------------------------
     CARD BEHAVIOUR (same logic as before, attached on first interaction)
     ------------------------------------------------------------------ */
  const setup = (card) => {
    const track = card.querySelector('.npc-product-card__image-track');
    const slides = Array.from(card.querySelectorAll('.npc-product-card__image-slide'));
    if (!track || !slides.length) return;

    const progress = card.querySelector('[data-npc-progress]');
    const progressThumb = card.querySelector('[data-npc-progress-thumb]');
    const productUrl = card.dataset.npcProductUrl;
    const title = card.querySelector('.npc-product-card__title');
    const imageCount = slides.length;

    let isPointerDown = false;
    let isHorizontalDragging = false;
    let hasMoved = false;
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let suppressClickUntil = 0;
    let isProgressDragging = false;

    /* ---------- positions ---------- */
    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const getImagePosition = (index) => {
      if (imageCount <= 1) return 0;
      const slide = slides[index];
      if (!slide) return 0;
      return Math.min(slide.offsetLeft, getMaxScroll());
    };

    const getCurrentImageIndex = () => {
      if (imageCount <= 1) return 0;
      const scrollLeft = track.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return closestIndex;
    };

    const goToImage = (index, smooth = true) => {
      const safeIndex = Math.max(0, Math.min(imageCount - 1, index));
      track.scrollTo({
        left: getImagePosition(safeIndex),
        behavior: smooth ? 'smooth' : 'auto'
      });
    };

    /* ---------- progress ---------- */
    const updateProgress = () => {
      if (!progressThumb) return;
      const maxScroll = getMaxScroll();
      const pct = maxScroll > 0 ? Math.max(0, Math.min(1, track.scrollLeft / maxScroll)) : 0;
      progressThumb.style.transform = `translate3d(${pct * THUMB_TRAVEL}%, 0, 0)`;
    };

    let progressFrame = null;
    const requestProgressUpdate = () => {
      if (progressFrame) return;
      progressFrame = requestAnimationFrame(() => {
        progressFrame = null;
        updateProgress();
      });
    };

    track.addEventListener('scroll', requestProgressUpdate, { passive: true });

    /* ---------- image slider drag ---------- */
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      isPointerDown = true;
      isHorizontalDragging = false;
      hasMoved = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = track.scrollLeft;

      event.stopPropagation();
    }, { passive: true });

    track.addEventListener('pointermove', (event) => {
      if (!isPointerDown) return;
      if (pointerId !== null && event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!isHorizontalDragging && !hasMoved && absX < DRAG_THRESHOLD && absY < DRAG_THRESHOLD) {
        event.stopPropagation();
        return;
      }

      if (absX >= DRAG_THRESHOLD || absY >= DRAG_THRESHOLD) {
        hasMoved = true;
        suppressClickUntil = Date.now() + CLICK_SUPPRESS_TIME;
      }

      if (absY > absX && !isHorizontalDragging) return;

      if (absX > absY && absX >= DRAG_THRESHOLD) {
        isHorizontalDragging = true;
        track.classList.add('npc-is-dragging');

        event.preventDefault();
        event.stopPropagation();

        const maxScroll = getMaxScroll();
        track.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft - deltaX));
      }
    }, { passive: false });

    const handleImagePointerEnd = (event) => {
      if (!isPointerDown) return;
      if (pointerId !== null && event.pointerId !== pointerId) return;

      const dragged = hasMoved || isHorizontalDragging;
      if (dragged) suppressClickUntil = Date.now() + CLICK_SUPPRESS_TIME;

      isPointerDown = false;
      isHorizontalDragging = false;
      hasMoved = false;
      pointerId = null;

      track.classList.remove('npc-is-dragging');
      event.stopPropagation();

      if (dragged) goToImage(getCurrentImageIndex(), true);
    };

    track.addEventListener('pointerup', handleImagePointerEnd, { passive: true });
    track.addEventListener('pointercancel', handleImagePointerEnd, { passive: true });

    track.addEventListener('lostpointercapture', () => {
      if (isPointerDown) suppressClickUntil = Date.now() + CLICK_SUPPRESS_TIME;
      isPointerDown = false;
      isHorizontalDragging = false;
      hasMoved = false;
      pointerId = null;
      track.classList.remove('npc-is-dragging');
    });

    /* ---------- click vs drag on images ---------- */
    track.addEventListener('click', (event) => {
      if (suppressClickUntil && Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        suppressClickUntil = 0;
      }
      /* Normal clicks fall through to the <a href> and open the product page. */
    }, true);

    // Native <img>/<a> drag-ghosts are blocked via draggable="false" + one delegated handler
    track.addEventListener('dragstart', (event) => {
      event.preventDefault();
      suppressClickUntil = Date.now() + CLICK_SUPPRESS_TIME;
    });

    /* ---------- progress bar interaction ---------- */
    if (progress && progressThumb) {
      const getProgressPosition = (clientX) => {
        const rect = progress.getBoundingClientRect();
        const thumbWidth = progressThumb.offsetWidth;
        const usableWidth = Math.max(1, rect.width - thumbWidth);
        let x = clientX - rect.left - (thumbWidth / 2);
        x = Math.max(0, Math.min(usableWidth, x));
        return x / usableWidth;
      };

      const updateImageFromProgress = (clientX, smooth = false) => {
        if (imageCount <= 1) return;
        const index = Math.round(getProgressPosition(clientX) * (imageCount - 1));
        goToImage(index, smooth);
      };

      const startProgressDrag = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        event.preventDefault();
        event.stopPropagation();
        isProgressDragging = true;

        try { progress.setPointerCapture(event.pointerId); } catch (error) {}

        updateImageFromProgress(event.clientX, false);
      };

      const moveProgressDrag = (event) => {
        if (!isProgressDragging) return;
        event.preventDefault();
        event.stopPropagation();
        updateImageFromProgress(event.clientX, false);
      };

      const endProgressDrag = (event) => {
        if (!isProgressDragging) return;
        event.preventDefault();
        event.stopPropagation();
        isProgressDragging = false;

        try {
          if (progress.hasPointerCapture(event.pointerId)) {
            progress.releasePointerCapture(event.pointerId);
          }
        } catch (error) {}

        goToImage(getCurrentImageIndex(), true);
        updateProgress();
      };

      progressThumb.addEventListener('pointerdown', startProgressDrag);
      progressThumb.addEventListener('pointermove', moveProgressDrag);
      progressThumb.addEventListener('pointerup', endProgressDrag);
      progressThumb.addEventListener('pointercancel', endProgressDrag);

      progress.addEventListener('pointerdown', (event) => {
        if (event.target.closest('[data-npc-progress-thumb]')) return;
        startProgressDrag(event);
      });
      progress.addEventListener('pointermove', moveProgressDrag);
      progress.addEventListener('pointerup', endProgressDrag);
      progress.addEventListener('pointercancel', endProgressDrag);

      progressThumb.addEventListener('keydown', (event) => {
        const currentIndex = getCurrentImageIndex();

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          goToImage(Math.min(imageCount - 1, currentIndex + 1), true);
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          goToImage(Math.max(0, currentIndex - 1), true);
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (productUrl) window.location.href = productUrl;
        }
      });
    }

    /* ---------- rating link ---------- */
    const rating = card.querySelector('.npc-product-card__rating');
    if (rating) rating.addEventListener('click', (event) => event.stopPropagation());

    /* ---------- title: click = product page, drag = parent slider ---------- */
    if (title) {
      let titlePointerDown = false;
      let titleMoved = false;
      let titleStartX = 0;
      let titleStartY = 0;
      let titlePointerId = null;

      title.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        titlePointerDown = true;
        titleMoved = false;
        titlePointerId = event.pointerId;
        titleStartX = event.clientX;
        titleStartY = event.clientY;
      }, { passive: true });

      title.addEventListener('pointermove', (event) => {
        if (!titlePointerDown) return;
        if (titlePointerId !== event.pointerId) return;

        const dx = Math.abs(event.clientX - titleStartX);
        const dy = Math.abs(event.clientY - titleStartY);

        if (dx >= DRAG_THRESHOLD || dy >= DRAG_THRESHOLD) {
          titleMoved = true;
          suppressClickUntil = Date.now() + CLICK_SUPPRESS_TIME;
        }
      }, { passive: true });

      const endTitlePointer = (event) => {
        if (!titlePointerDown) return;
        if (titlePointerId !== event.pointerId) return;
        if (titleMoved) suppressClickUntil = Date.now() + CLICK_SUPPRESS_TIME;
        titlePointerDown = false;
        titleMoved = false;
        titlePointerId = null;
      };

      title.addEventListener('pointerup', endTitlePointer, { passive: true });
      title.addEventListener('pointercancel', endTitlePointer, { passive: true });

      title.addEventListener('click', (event) => {
        if (suppressClickUntil && Date.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          suppressClickUntil = 0;
          return;
        }
        event.stopPropagation();
      }, true);
    }

    updateProgress();
  };

  /* ------------------------------------------------------------------
     ACTIVATION (delegated — one listener for the whole page)
     ------------------------------------------------------------------ */
  const cardFrom = (event) => {
    const target = event.target;
    return target instanceof Element ? target.closest(CARD) : null;
  };

  const activate = (event) => {
    const card = cardFrom(event);
    if (!card || activated.has(card)) return;
    activated.add(card);
    loadAll(card);
    setup(card);
  };

  // Capture phase so listeners are in place before the same event reaches the card.
  document.addEventListener('pointerdown', activate, { capture: true, passive: true });
  document.addEventListener('focusin', activate, { capture: true, passive: true });

  // Mouse hover / touch contact: fetch just the 2nd image so the first swipe isn't blank.
  document.addEventListener('pointerover', (event) => {
    const card = cardFrom(event);
    if (card) warm(card);
  }, { capture: true, passive: true });

  /* ------------------------------------------------------------------
     OPTIONAL WARM-UP: after the shopper first interacts with the PAGE
     (scroll / touch / key), fetch the 2nd image of the cards on screen.
     Nothing extra is downloaded for non-interacting visitors or lab tests.
     ------------------------------------------------------------------ */
  const visible = new Set();
  let pageInteracted = false;
  let io = null;

  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          visible.add(entry.target);
          if (pageInteracted) warm(entry.target);
        } else {
          visible.delete(entry.target);
        }
      });
    }, { rootMargin: '150px' });
  }

  const observe = () => {
    if (!io) return;
    document.querySelectorAll(CARD).forEach((card) => io.observe(card));
  };

  const warmEvents = ['scroll', 'touchstart', 'pointermove', 'keydown'];
  const onFirstPageInteraction = () => {
    pageInteracted = true;
    warmEvents.forEach((type) => window.removeEventListener(type, onFirstPageInteraction));
    const run = () => visible.forEach(warm);
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 300);
  };
  warmEvents.forEach((type) => window.addEventListener(type, onFirstPageInteraction, { passive: true }));

  observe();

  // Re-scan after dynamic content (theme editor, AJAX filters, infinite scroll)
  document.addEventListener('shopify:section:load', observe);
  window.NPCProductCards = { refresh: observe };
})();